import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { Creator } from 'routes/Upsell/BusTicketCreator/styles'
import { BusRoute } from 'routes/Upsell/BusTicketCreator/fields/BusRoute'
import { BusDestination } from 'routes/Upsell/BusTicketCreator/fields/BusDestination'
import {
    BusDatesAndHours,
    RouteDetails,
} from 'routes/Upsell/BusTicketCreator/fields/BusDatesAndHours'
import EditorSummary from './EditorSummary'
import Loader from 'components/shared/Loader'
import { useAutoScroll } from 'utils/hooks'
import ShuttleInfo from 'routes/Upsell/BusTicketCreator/fields/ShuttleInfo'
import { EditReservationTransportDetailDto } from 'store/api-slice/hooks'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import { checkIfCompleted } from 'store/transport-slice'
import { ChangesDataAttr } from 'routes/selfcare/views/ManageOrder/tickets/BusTicket'
import { TimeBetweenTripsWarning } from 'routes/Upsell/BusTicketCreator/fields/TimeBetweenTripsWarning'
import Info from 'components/shared/Info'
import { TransportBlockingError } from 'routes/Upsell/types'
import { TransportProductEditResponseDto } from 'store/selfcare-slice/types'

interface BusTicketEditorProps {
    newQuantity: number
    currentPrice: number
    handleSetQuantity: (quantity: number) => void
    handleCancel: () => void
    unitPrice?: number
    ticket: TransportProductEditResponseDto
    routeDetails: RouteDetails
    setRouteDetails: (data: RouteDetails) => void
    setChanges: (data: ChangesDataAttr) => void
    isModified: boolean
    index: number
    isRequiresTimeSlotOriginal?: boolean
}

const BusTicketEditor = ({
    currentPrice,
    handleSetQuantity,
    handleCancel,
    newQuantity,
    unitPrice,
    ticket,
    routeDetails,
    setRouteDetails,
    setChanges,
    isModified,
    index,
    isRequiresTimeSlotOriginal,
}: BusTicketEditorProps) => {
    const { formatMessage } = useIntl()

    const { animatePendingScroll, scrollIndex } = useAutoScroll()
    const { editItem, isEditProcessing } = useSelfcare()

    const [isBusRouteDataFetched, setIsBusRouteDataFetched] = useState(false)
    const [isDeparturePlacesDataFetched, setIsDeparturePlacesDataFetched] =
        useState(false)
    const [isTimeBetweenTripsTooShort, setIsTimeBetweenTripsTooShort] =
        useState(false)

    const [blockingError, setBlockingError] =
        useState<TransportBlockingError>(null)

    const [isEditable, setIsEditable] = useState<boolean | undefined>(undefined)
    const [isTimeSlotChangeable, setIsTimeSlotChangeable] = useState<
        boolean | undefined
    >(undefined)

    const areInitialDataFetched =
        isBusRouteDataFetched &&
        isDeparturePlacesDataFetched &&
        blockingError !== 'calendar'

    const newPrice = unitPrice && isModified ? unitPrice : null

    const { detail, quantity } = ticket || {}
    const {
        date,
        departurePlace,
        routeType,
        returnDate,
        time,
        returnTime,
        boxId,
        isShuttle,
        orderTransportTicketId,
        isRequiresPurchaseTimeSlot,
    } = detail || {}

    const editedDetails: EditReservationTransportDetailDto = {
        boxId,
        date,
        departurePlace,
        isShuttle,
        orderTransportTicketId,
        returnDate,
        returnTime,
        routeType,
        time,
        isRequiresPurchaseTimeSlot,
    }

    const isTicketCompleted = checkIfCompleted(
        routeType,
        departurePlace,
        date || returnDate,
        returnDate || 'null',
        time || '',
        returnTime || '',
        isRequiresPurchaseTimeSlot ?? true
    )

    const NonEditableMsg = (
        <Info color="error">
            {formatMessage({
                id: 'upsells.transport.msg_non_editable_ticket',
                defaultMessage: 'Nie można edytować tego biletu autobusowego.',
            })}
        </Info>
    )

    const handleChanges = (data: ChangesDataAttr) => {
        setChanges(data)
    }

    useEffect(() => {
        if (routeType === 'to_from_park' && time && returnTime) {
            const dateFrom = moment(`${date} ${time}`, 'YYYY_MM_DD hh:mm')
            const dateTo = moment(
                `${returnDate} ${returnTime}`,
                'YYYY_MM_DD hh:mm'
            )
            const duration = moment.duration(dateTo.diff(dateFrom))
            const hours = duration.asHours()

            if (hours <= 1) {
                setIsTimeBetweenTripsTooShort(true)
            } else {
                setIsTimeBetweenTripsTooShort(false)
            }
        } else {
            setIsTimeBetweenTripsTooShort(false)
        }
    }, [time, returnTime])

    useEffect(() => {
        animatePendingScroll(`transport-ticket-${boxId}`)
    }, [scrollIndex])

    return (
        <Creator
            isFullWidth
            pt={8}
            pb={8}
            gap={40}
            id={`transport-ticket-${boxId}`}
        >
            <BusRoute
                routeId={boxId}
                routeOption={routeType}
                setRouteOption={(value) => handleChanges({ routeType: value })}
                setIsDataFetched={setIsBusRouteDataFetched}
                testIdPrefix={`selfcare-transport-${index}`}
            />

            {isBusRouteDataFetched && (
                <BusDestination
                    routeId={boxId}
                    startCity={departurePlace}
                    setStartCity={(departurePlace) =>
                        handleChanges({ departurePlace })
                    }
                    routeOption={routeType}
                    duration={routeDetails.duration || ''}
                    unitPrice={routeDetails.unitPrice || null}
                    setIsDataFetched={setIsDeparturePlacesDataFetched}
                    testIdPrefix={`selfcare-transport-${index}`}
                />
            )}

            {blockingError === 'calendar' && <Loader />}

            {areInitialDataFetched && (
                <>
                    {routeType && departurePlace && (
                        <BusDatesAndHours
                            routeIndex={0}
                            routeId={boxId}
                            routeType={routeType}
                            departurePlace={departurePlace}
                            ticketsQuantity={quantity || 1}
                            dateTo={date}
                            dateFrom={returnDate || date}
                            setDateTo={(index, date) => handleChanges({ date })}
                            setDateFrom={(index, returnDate) =>
                                handleChanges({ returnDate })
                            }
                            timeTo={time}
                            timeFrom={returnTime}
                            setTimeTo={(index, time) => handleChanges({ time })}
                            setTimeFrom={(index, returnTime) =>
                                handleChanges({ returnTime })
                            }
                            setSeatsLimit={() => null}
                            setError={(value) => setBlockingError(value)}
                            isBookingConflictError={false}
                            setRouteDetails={setRouteDetails}
                            edited={{
                                // mechanism adopted from editing tickets in creator
                                // - simplified data for selfcare
                                index: -1,
                                origin: {
                                    ticket: {
                                        cartItemId: '',
                                        productId: '',
                                        productType: 'bus_ticket',
                                        quantity: 1,
                                        detail: { ...editedDetails },
                                    },
                                    isCompleted: true,
                                    isSubmitted: true,
                                    duration: '',
                                    unitPrice: null,
                                    defaultDates: [],
                                    defaultQuantity: 1,
                                },
                            }}
                            isEditable={isEditable}
                            setIsEditable={setIsEditable}
                            isTimeSlotChangeable={isTimeSlotChangeable}
                            setIsTimeSlotChangeable={setIsTimeSlotChangeable}
                            testIdPrefix="selfcare-transport"
                            isRequiresTimeSlotOriginal={
                                isRequiresTimeSlotOriginal
                            }
                        />
                    )}

                    {isRequiresPurchaseTimeSlot === false &&
                        !blockingError &&
                        isTimeSlotChangeable && <ShuttleInfo />}

                    {isTimeBetweenTripsTooShort && <TimeBetweenTripsWarning />}

                    {isEditable === false && NonEditableMsg}

                    <EditorSummary
                        newPrice={newPrice}
                        currentPrice={currentPrice}
                        handleCancel={handleCancel}
                        updatedTicket={{
                            productType: 'bus_ticket',
                            detail: editedDetails,
                        }}
                        isSavingDisabled={
                            !isModified ||
                            !isTicketCompleted ||
                            blockingError !== null ||
                            isTimeSlotChangeable === false ||
                            !isEditable
                        }
                        editItem={editItem}
                        isFetching={isEditProcessing}
                        index={index}
                        testIdPrefix="selfcare-transport"
                    />
                </>
            )}
        </Creator>
    )
}

export default BusTicketEditor
